import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { 
  Loader2, Search, Users, UserPlus, Clock, UserCheck, ChevronRight, Crown, Sparkles, Flame, Dumbbell, Heart, Target, Zap
} from 'lucide-react';
import { useConfirmDialog } from '../components/ui/ConfirmDialog';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { NavBar } from '../components/NavBar';
import { getImageUrl } from '../utils/imageUtils';
import { getDifficultyLabel } from '../utils/constants';

const API = process.env.REACT_APP_BACKEND_URL;

// Map raw slugs to friendly labels + icon so goal chips read cleanly
const GOAL_META = {
  weight_loss: { label: 'Weight Loss', Icon: Flame },
  muscle_building: { label: 'Muscle Building', Icon: Dumbbell },
  endurance: { label: 'Endurance', Icon: Heart },
  general_fitness: { label: 'General Fitness', Icon: Target },
  strength: { label: 'Strength', Icon: Zap },
};

const prettifyGoal = (g) => GOAL_META[g]?.label || g.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

export const DiscoverPage = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [skip, setSkip] = useState(0);
  const [followingId, setFollowingId] = useState(null);
  const [buddyRequestingId, setBuddyRequestingId] = useState(null);
  const { confirm, dialog: confirmDialog } = useConfirmDialog();

  useEffect(() => {
    fetchUsers(true);
  }, []);

  const fetchUsers = async (reset = false) => {
    if (reset) {
      setLoading(true);
      setSkip(0);
    } else {
      setLoadingMore(true);
    }

    try {
      const currentSkip = reset ? 0 : skip;
      const params = new URLSearchParams({
        skip: currentSkip,
        limit: 20
      });
      if (search) params.append('search', search);

      const res = await api().get(`/social/discover?${params}`);
      
      if (reset) {
        setUsers(res.data.users);
      } else {
        setUsers(prev => [...prev, ...res.data.users]);
      }
      
      setTotal(res.data.total);
      setHasMore(res.data.has_more);
      setSkip(currentSkip + res.data.users.length);
    } catch (err) {
      toast.error('Failed to load users');
    }
    
    setLoading(false);
    setLoadingMore(false);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchUsers(true);
  };

  const handleFollow = async (userId, currentStatus) => {
    // Confirm on unfollow (accepted follows only) — this is a common mis-tap
    // on mobile since the "Following" pill sits right where the "Follow"
    // pill was, so we protect the destructive action behind a prompt.
    if (currentStatus === 'accepted') {
      const target = users.find((u) => u.id === userId);
      const name = target?.name || 'this user';
      const ok = await confirm({
        title: `Unfollow ${name}?`,
        description: "You'll stop seeing their posts and workout activity in your feed.",
        confirmLabel: 'Unfollow',
        cancelLabel: 'Keep Following',
        tone: 'danger',
      });
      if (!ok) return;
    }
    setFollowingId(userId);
    try {
      if (currentStatus === 'accepted' || currentStatus === 'pending') {
        await api().delete(`/social/follow/${userId}`);
        toast.success(currentStatus === 'accepted' ? 'Unfollowed' : 'Request cancelled');
      } else {
        await api().post('/social/follow', { user_id: userId });
        toast.success('Follow request sent!');
      }
      
      // Update local state
      setUsers(prev => prev.map(u => {
        if (u.id === userId) {
          return {
            ...u,
            follow_status: currentStatus ? null : 'pending'
          };
        }
        return u;
      }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update follow');
    }
    setFollowingId(null);
  };

  const handleSendBuddy = async (userId) => {
    setBuddyRequestingId(userId);
    try {
      const res = await api().post('/buddies/request', { user_id: userId });
      if (res.data?.status === 'accepted') {
        toast.success("You're already buddies!");
      } else {
        toast.success('Buddy request sent!');
      }
      fetchUsers(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send buddy request');
    }
    setBuddyRequestingId(null);
  };

  const handleAcceptBuddy = async (userId, requestId) => {
    if (!requestId) {
      toast.error('Missing request ID');
      return;
    }
    setBuddyRequestingId(userId);
    try {
      await api().post(`/buddies/requests/${requestId}/accept`);
      toast.success('Buddy request accepted!');
      fetchUsers(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to accept buddy request');
    }
    setBuddyRequestingId(null);
  };

  const getBuddyButton = (user) => {
    if (user.buddy_status === 'accepted') {
      return (
        <button disabled className="w-full inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-primary bg-primary/[0.08] border border-primary/30 rounded-full px-3 py-2 opacity-100 cursor-default" data-testid={`buddy-accepted-${user.id}`}>
          <UserCheck className="h-3.5 w-3.5" /> Buddies
        </button>
      );
    }
    if (user.buddy_status === 'pending') {
      const isLoading = buddyRequestingId === user.id;
      if (user.buddy_request_outgoing) {
        return (
          <button disabled className="w-full inline-flex items-center justify-center gap-1.5 text-xs font-medium text-muted-foreground bg-white/[0.03] border border-white/10 rounded-full px-3 py-2" data-testid={`buddy-pending-out-${user.id}`}>
            <Clock className="h-3.5 w-3.5" />
            Buddy Sent
          </button>
        );
      }
      return (
        <button
          onClick={() => handleAcceptBuddy(user.id, user.buddy_request_id)}
          disabled={isLoading || !user.buddy_request_id}
          className="w-full inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-black bg-orange-400 hover:bg-orange-300 rounded-full px-3 py-2 transition-all shadow-[0_4px_14px_rgba(251,146,60,0.25)]"
          data-testid={`buddy-accept-card-${user.id}`}
        >
          {isLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (
            <>
              <UserCheck className="h-3.5 w-3.5" /> Accept Buddy
            </>
          )}
        </button>
      );
    }
    if (user.follow_status === 'accepted' && user.follows_me) {
      const isLoading = buddyRequestingId === user.id;
      return (
        <button
          onClick={() => handleSendBuddy(user.id)}
          disabled={isLoading}
          className="w-full inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-black bg-orange-400 hover:bg-orange-300 rounded-full px-3 py-2 transition-all shadow-[0_4px_14px_rgba(251,146,60,0.25)]"
          data-testid={`add-buddy-btn-${user.id}`}
        >
          {isLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (
            <>
              <Users className="h-3.5 w-3.5" /> Add Buddy
            </>
          )}
        </button>
      );
    }
    return null;
  };

  const getFollowButton = (user) => {
    const isLoading = followingId === user.id;

    if (user.follow_status === 'accepted') {
      return (
        <button
          onClick={() => handleFollow(user.id, 'accepted')}
          disabled={isLoading}
          className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-medium text-white bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 hover:border-white/25 rounded-full px-3 py-2 transition-all"
          data-testid={`unfollow-btn-${user.id}`}
        >
          {isLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (
            <>
              <UserCheck className="h-3.5 w-3.5" />
              Following
            </>
          )}
        </button>
      );
    }

    if (user.follow_status === 'pending') {
      return (
        <button
          onClick={() => handleFollow(user.id, 'pending')}
          disabled={isLoading}
          className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-medium text-yellow-300 bg-yellow-500/[0.06] hover:bg-yellow-500/[0.12] border border-yellow-500/30 hover:border-yellow-500/50 rounded-full px-3 py-2 transition-all"
          data-testid={`pending-btn-${user.id}`}
        >
          {isLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (
            <>
              <Clock className="h-3.5 w-3.5" />
              Requested
            </>
          )}
        </button>
      );
    }

    return (
      <button
        onClick={() => handleFollow(user.id, null)}
        disabled={isLoading}
        className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-black bg-primary hover:bg-primary/90 rounded-full px-3 py-2 transition-all shadow-[0_4px_14px_rgba(250,204,21,0.2)]"
        data-testid={`follow-btn-${user.id}`}
      >
        {isLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (
          <>
            <UserPlus className="h-3.5 w-3.5" />
            Follow
          </>
        )}
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-black" data-testid="discover-page">
      {confirmDialog}
      <NavBar title="Discover" />

      <main className="container mx-auto px-4 sm:px-6 py-8 max-w-7xl">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-primary bg-primary/[0.08] border border-primary/25 rounded-full px-2.5 py-1 mb-3">
            <Sparkles className="h-3 w-3" /> Community
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl uppercase tracking-wider leading-[1.05] bg-gradient-to-b from-white to-white/70 bg-clip-text text-transparent">
            Discover <span className="text-primary">People</span>
          </h1>
          <p className="text-muted-foreground mt-2 text-sm sm:text-base max-w-xl">
            Connect with other fitness enthusiasts, follow their journey, and stay motivated together.
          </p>
        </motion.div>

        {/* Search + Stats bar */}
        <div className="mb-8 relative rounded-2xl border border-white/10 bg-gradient-to-b from-zinc-900/70 to-black/50 p-4 sm:p-5 shadow-[0_4px_20px_rgba(0,0,0,0.25)] overflow-hidden">
          <div className="pointer-events-none absolute -top-16 -right-16 h-40 w-40 rounded-full bg-primary/5 blur-3xl" />
          <div className="relative">
            <form onSubmit={handleSearch}>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by name…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-10 h-11 bg-black/60 border-white/10 focus:border-primary text-white rounded-full"
                    data-testid="search-input"
                  />
                </div>
                <button
                  type="submit"
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-black bg-primary hover:bg-primary/90 rounded-full px-5 h-11 shadow-[0_4px_14px_rgba(250,204,21,0.25)] transition-all"
                  data-testid="search-btn"
                >
                  <Search className="h-4 w-4" />
                  <span className="hidden sm:inline">Search</span>
                </button>
              </div>
            </form>
            <div className="flex items-center gap-2 mt-3">
              <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-primary bg-primary/[0.08] border border-primary/25 rounded-full px-2.5 py-0.5">
                <Users className="h-3 w-3" />
                {total} {total === 1 ? 'member' : 'members'}
              </span>
            </div>
          </div>
        </div>

        {/* Users Grid */}
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-12 w-12 text-primary animate-spin" />
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-14 rounded-2xl border border-dashed border-white/15 bg-white/[0.02] max-w-lg mx-auto">
            <div className="h-14 w-14 rounded-full bg-primary/10 border border-primary/25 flex items-center justify-center mx-auto mb-4">
              <Users className="h-6 w-6 text-primary" />
            </div>
            <h3 className="font-heading text-lg text-white uppercase tracking-wider mb-1">No Users Found</h3>
            <p className="text-muted-foreground text-sm">
              {search ? 'Try a different search term' : 'Be the first to join!'}
            </p>
          </div>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {users.map((user, i) => (
                <motion.div
                  key={user.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25, delay: i * 0.03 }}
                  className="group relative rounded-2xl border border-white/10 bg-gradient-to-b from-zinc-900/80 to-black/60 p-5 shadow-[0_4px_20px_rgba(0,0,0,0.25)] hover:border-primary/30 hover:shadow-[0_8px_30px_rgba(250,204,21,0.08)] transition-all overflow-hidden"
                  data-testid={`user-card-${user.id}`}
                >
                  <div className="pointer-events-none absolute -top-16 -right-16 h-32 w-32 rounded-full bg-primary/[0.05] blur-3xl opacity-0 group-hover:opacity-100 transition-opacity" />

                  <div className="relative flex flex-col items-center text-center">
                    {/* Avatar */}
                    <Link to={`/user/${user.id}`} className="relative mb-3">
                      {user.is_premium && (
                        <span className="pointer-events-none absolute inset-0 -m-1 rounded-full bg-gradient-to-tr from-yellow-500/40 via-yellow-300/15 to-yellow-500/40 blur-md" />
                      )}
                      <Avatar className={`relative h-20 w-20 ${user.is_premium ? 'ring-2 ring-yellow-400/70 ring-offset-4 ring-offset-black' : 'ring-2 ring-primary/30 ring-offset-4 ring-offset-black'} hover:ring-primary transition-all`}>
                        <AvatarImage src={getImageUrl(user.profile_picture)} />
                        <AvatarFallback className="bg-primary/20 text-primary text-2xl">
                          {user.name?.charAt(0) || 'U'}
                        </AvatarFallback>
                      </Avatar>
                    </Link>

                    {/* Premium pill */}
                    {user.is_premium && (
                      <span
                        className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.15em] text-yellow-300 bg-yellow-500/[0.1] border border-yellow-500/40 rounded-full px-2 py-0.5 mb-2"
                        data-testid={`premium-badge-${user.id}`}
                      >
                        <Crown className="h-2.5 w-2.5" /> Premium
                      </span>
                    )}

                    {/* Name */}
                    <Link
                      to={`/user/${user.id}`}
                      className="font-heading text-base text-white uppercase tracking-wider hover:text-primary transition-colors leading-tight line-clamp-1"
                    >
                      {user.name}
                    </Link>

                    {/* Experience Level chip */}
                    {user.experience_level && (
                      <span className="mt-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground bg-white/[0.03] border border-white/10 rounded-full px-2 py-0.5">
                        {getDifficultyLabel(user.experience_level)}
                      </span>
                    )}

                    {/* Followers count */}
                    <p className="text-muted-foreground text-xs mt-2">
                      <span className="text-white/80 font-semibold">{user.followers_count}</span> {user.followers_count === 1 ? 'follower' : 'followers'}
                    </p>

                    {/* Fitness Goals */}
                    {user.fitness_goals && user.fitness_goals.length > 0 && (
                      <div className="flex flex-wrap justify-center gap-1 mt-3">
                        {user.fitness_goals.slice(0, 2).map(goal => {
                          const meta = GOAL_META[goal];
                          const Icon = meta?.Icon;
                          return (
                            <span
                              key={goal}
                              className="inline-flex items-center gap-1 text-[10px] font-medium text-primary bg-primary/[0.08] border border-primary/25 rounded-full px-2 py-0.5"
                            >
                              {Icon && <Icon className="h-2.5 w-2.5" />}
                              {prettifyGoal(goal)}
                            </span>
                          );
                        })}
                        {user.fitness_goals.length > 2 && (
                          <span className="inline-flex items-center text-[10px] font-medium text-white/60 bg-white/[0.03] border border-white/10 rounded-full px-2 py-0.5">
                            +{user.fitness_goals.length - 2}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-1.5 w-full mt-4">
                      {getFollowButton(user)}
                      <Link to={`/user/${user.id}`} className="flex-1">
                        <button className="w-full inline-flex items-center justify-center gap-1 text-xs font-medium text-muted-foreground hover:text-white bg-white/[0.02] hover:bg-white/[0.06] border border-white/10 hover:border-white/25 rounded-full px-3 py-2 transition-all">
                          View
                          <ChevronRight className="h-3.5 w-3.5" />
                        </button>
                      </Link>
                    </div>
                    {getBuddyButton(user) && (
                      <div className="w-full mt-1.5">
                        {getBuddyButton(user)}
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Load More */}
            {hasMore && (
              <div className="text-center mt-10">
                <button
                  onClick={() => fetchUsers(false)}
                  disabled={loadingMore}
                  className="inline-flex items-center gap-2 text-sm font-medium text-white bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 hover:border-primary/30 rounded-full px-6 py-3 transition-all disabled:opacity-60"
                  data-testid="load-more-btn"
                >
                  {loadingMore ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading…
                    </>
                  ) : (
                    <>Load More <ChevronRight className="h-4 w-4" /></>
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default DiscoverPage;
